enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  context?: string;
  data?: any;
  userId?: number;
  url?: string;
  userAgent?: string;
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel = LogLevel.INFO;
  private isProduction = import.meta.env.MODE === 'production';
  private logQueue: LogEntry[] = [];
  private maxQueueSize = 100;
  private flushInterval = 10000;
  private userId: number | null = null;
  private sensitiveKeys = new Set([
    'card',
    'cardnumber',
    'card_number',
    'pan',
    'cvv',
    'cvc',
    'securitycode',
    'security_code',
    'expiry',
    'exp',
    'expmonth',
    'expyear',
    'billing_details',
    'billingdetails',
    'billing_address',
    'billingaddress',
    'paymentmethod',
    'paymentmethodid',
    'payment_method',
    'payment_method_id',
    'paymentintent',
    'paymentintentid',
    'payment_intent',
    'payment_intent_id',
    'clientsecret',
    'setupintentid',
    'last4',
  ]);

  private constructor() {
    if (this.isProduction) {
      this.logLevel = LogLevel.ERROR;
    } else {
      this.logLevel = LogLevel.DEBUG;
    }

    if (typeof window !== 'undefined') {
      this.startFlushInterval();
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  setUserId(userId: number) {
    this.userId = userId;
  }

  setLogLevel(level: string) {
    switch (level.toUpperCase()) {
      case 'DEBUG':
        this.logLevel = LogLevel.DEBUG;
        break;
      case 'INFO':
        this.logLevel = LogLevel.INFO;
        break;
      case 'WARN':
        this.logLevel = LogLevel.WARN;
        break;
      case 'ERROR':
        this.logLevel = LogLevel.ERROR;
        break;
      case 'NONE':
        this.logLevel = LogLevel.NONE;
        break;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatMessage(level: string, message: string, context?: string, data?: any): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data: this.redactSensitiveData(data),
      userId: this.userId || undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
  }

  private redactSensitiveData(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.redactSensitiveData(item));
    }

    if (!value || typeof value !== 'object') {
      return value;
    }

    return Object.entries(value).reduce<Record<string, any>>((acc, [key, entryValue]) => {
      const normalizedKey = key.replace(/[^a-z0-9]/gi, '').toLowerCase();
      if (this.sensitiveKeys.has(normalizedKey)) {
        acc[key] = '[REDACTED]';
        return acc;
      }

      acc[key] = this.redactSensitiveData(entryValue);
      return acc;
    }, {});
  }

  private logToConsole(level: LogLevel, entry: LogEntry) {
    const logMethod = {
      [LogLevel.DEBUG]: console.debug,
      [LogLevel.INFO]: console.info,
      [LogLevel.WARN]: console.warn,
      [LogLevel.ERROR]: console.error,
    }[level];

    const message = `[${entry.timestamp}] [${entry.level}]${entry.context ? ` [${entry.context}]` : ''} ${entry.message}`;

    if (entry.data) {
      logMethod(message, entry.data);
    } else {
      logMethod(message);
    }
  }

  private addToQueue(entry: LogEntry) {
    this.logQueue.push(entry);

    if (this.logQueue.length >= this.maxQueueSize) {
      this.flush();
    }
  }

  private async flush() {
    if (this.logQueue.length === 0) return;

    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    try {
      if (this.isProduction) {
        await fetch('/api/logs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ logs: logsToSend }),
          keepalive: true,
        });
      }
    } catch (error) {
      if (!this.isProduction) {
        console.error('Failed to send logs to server:', error);
      }
    }
  }

  private startFlushInterval() {
    setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  debug(message: string, context?: string, data?: any) {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const entry = this.formatMessage('DEBUG', message, context, data);
    this.logToConsole(LogLevel.DEBUG, entry);
    this.addToQueue(entry);
  }

  info(message: string, context?: string, data?: any) {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const entry = this.formatMessage('INFO', message, context, data);
    this.logToConsole(LogLevel.INFO, entry);
    this.addToQueue(entry);
  }

  warn(message: string, context?: string, data?: any) {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const entry = this.formatMessage('WARN', message, context, data);
    this.logToConsole(LogLevel.WARN, entry);
    this.addToQueue(entry);
  }

  error(message: string, context?: string, error?: Error | any, data?: any) {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const errorData = error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...data,
    } : error;

    const entry = this.formatMessage('ERROR', message, context, errorData);
    this.logToConsole(LogLevel.ERROR, entry);
    this.addToQueue(entry);
  }

  logApiCall(config: any) {
    const { method, url, data, params } = config;
    this.info(
      `API Request: ${method?.toUpperCase()} ${url}`,
      'API',
      { data, params }
    );
  }

  logApiResponse(response: any) {
    const { status, config } = response;
    const { method, url } = config;
    this.info(
      `API Response: ${method?.toUpperCase()} ${url} - Status: ${status}`,
      'API'
    );
  }

  logApiError(error: any) {
    const { config, response } = error;
    const { method, url } = config || {};
    const status = response?.status;
    const message = response?.data?.message || error.message;

    this.error(
      `API Error: ${method?.toUpperCase()} ${url} - Status: ${status}`,
      'API',
      error,
      { status, message }
    );
  }

  logUserAction(action: string, details?: any) {
    this.info(
      `User Action: ${action}`,
      'USER_ACTION',
      details
    );
  }

  logErrorBoundary(error: Error, errorInfo: any) {
    this.error(
      'React Error Boundary caught an error',
      'ErrorBoundary',
      error,
      {
        componentStack: errorInfo.componentStack,
      }
    );
  }
}

export const logger = Logger.getInstance();
